import type { DesignLayer } from '@pz/scene';
import { el, type DesignerCtx } from './panels.js';
import { openPopover } from './overlay.js';
import { canDelete, run } from './commands.js';

/**
 * The right-click (and long-press) menu on the canvas, item for item as the
 * reference editor has it: Kopiraj · Nalepi · Obriši · Pošalji nazad · Korak
 * nazad · Korak napred · Dovedi napred · Prikaži slojeve.
 *
 * Paint order is the one thing here a shopper cannot do by dragging, which is
 * why four of the eight items are about it. Everything runs through the same
 * command path as the toolbar, so the menu can never disagree with it about
 * what is permitted.
 */

export interface ContextMenuOptions {
  ctx: DesignerCtx;
  /** Where the pointer was, in client coordinates. */
  at: { clientX: number; clientY: number };
  /** The layer under the pointer, if any. */
  layer: DesignLayer | undefined;
  /** The element focus returns to when the menu closes. */
  anchor: HTMLElement;
}

export function openContextMenu(options: ContextMenuOptions): void {
  const { ctx, layer } = options;
  const ids = (): string[] => {
    if (ctx.selection.size) return [...ctx.selection];
    return layer ? [layer.id] : [];
  };

  openPopover({
    ctx,
    anchor: options.anchor,
    at: options.at,
    className: 'is-menu',
    build: (body, handle) => {
      const item = (label: string, onPick: () => void, disabled = false): void => {
        const button = el('button', 'pzd-menu-item');
        button.type = 'button';
        button.textContent = label;
        button.disabled = disabled;
        button.addEventListener('click', () => {
          onPick();
          // Every item is a completed action; leaving the menu up afterwards
          // only invites a second, accidental one.
          handle.close();
        });
        body.append(button);
      };
      // On empty canvas there is nothing to copy, delete or reorder, so the
      // menu is the two items that still mean something rather than six greyed
      // rows explaining that nothing is selected.
      if (!ids().length) {
        item(ctx.texts.menuPaste, () => ctx.pasteSelection());
        item(ctx.texts.menuShowLayers, () => ctx.openTool('layers'));
        return;
      }

      item(ctx.texts.menuCopy, () => ctx.copySelection());
      item(ctx.texts.menuPaste, () => ctx.pasteSelection());
      item(ctx.texts.menuDelete, () => run(ctx, { op: 'delete', ids: ids() }),
        !ids().every((id) => canDelete(ctx.store.value().layers.find((item2) => item2.id === id))));
      body.append(el('div', 'pzd-menu-sep'));
      item(ctx.texts.menuSendBack, () => run(ctx, { op: 'order', ids: ids(), direction: 'back' }));
      item(ctx.texts.menuBackward, () => run(ctx, { op: 'order', ids: ids(), direction: 'backward' }));
      item(ctx.texts.menuForward, () => run(ctx, { op: 'order', ids: ids(), direction: 'forward' }));
      item(ctx.texts.menuFront, () => run(ctx, { op: 'order', ids: ids(), direction: 'front' }));
      body.append(el('div', 'pzd-menu-sep'));
      item(ctx.texts.menuShowLayers, () => ctx.openTool('layers'));
    },
  });
}
